import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import ThemeToggle from '../components/ThemeToggle';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      {/* Theme Toggle - Top Right */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      {/* Back Button - Top Left */}
      <div className="absolute top-4 left-4 z-10">
        <Link
          to="/login"
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Voltar
        </Link>
      </div>

      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
            Política de Privacidade
          </h1>

          <div className="prose prose-gray dark:prose-invert max-w-none">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Última atualização: 26 de agosto de 2025
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                1. Introdução
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Esta Política de Privacidade descreve como o Upmindy coleta, usa, armazena e protege suas 
                informações pessoais quando você utiliza nossa plataforma de aprendizado. Levamos sua 
                privacidade a sério e estamos comprometidos em proteger suas informações pessoais.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                2. Informações que Coletamos
              </h2>
              <div className="text-gray-700 dark:text-gray-300 space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  2.1 Informações Fornecidas por Você
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Nome completo</li>
                  <li>Endereço de e-mail</li>
                  <li>Senha (armazenada de forma criptografada)</li>
                  <li>Conteúdo dos flashcards que você cria</li>
                  <li>Preferências de estudo e configurações da conta</li>
                </ul>

                <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-6">
                  2.2 Informações Coletadas Automaticamente
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Dados de uso da plataforma (tempo de estudo, frequência de acesso)</li>
                  <li>Informações do dispositivo (tipo de dispositivo, sistema operacional)</li>
                  <li>Dados de localização aproximada (baseados no endereço IP)</li>
                  <li>Cookies e tecnologias similares para melhorar a experiência do usuário</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                3. Como Usamos Suas Informações
              </h2>
              <div className="text-gray-700 dark:text-gray-300 space-y-4">
                <p>Utilizamos suas informações para:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Fornecer e manter nossos serviços de aprendizado</li>
                  <li>Personalizar sua experiência de estudo</li>
                  <li>Processar seu registro e gerenciar sua conta</li>
                  <li>Enviar notificações importantes sobre o serviço</li>
                  <li>Melhorar nossos algoritmos de repetição espaçada</li>
                  <li>Analisar o uso da plataforma para melhorias</li>
                  <li>Responder a suas solicitações de suporte</li>
                  <li>Cumprir obrigações legais e regulamentares</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                4. Compartilhamento de Informações
              </h2>
              <div className="text-gray-700 dark:text-gray-300 space-y-4">
                <p>
                  Não vendemos, trocamos ou transferimos suas informações pessoais para terceiros sem 
                  sua permissão, exceto nas seguintes circunstâncias:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Com provedores de serviços confiáveis que nos ajudam a operar a plataforma</li>
                  <li>Quando exigido por lei ou autoridades competentes</li>
                  <li>Para proteger nossos direitos, propriedade ou segurança</li>
                  <li>Com seu consentimento explícito</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                5. Segurança dos Dados
              </h2>
              <div className="text-gray-700 dark:text-gray-300 space-y-4">
                <p>
                  Implementamos medidas de segurança técnicas, administrativas e físicas para proteger 
                  suas informações pessoais, incluindo:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Criptografia de dados em trânsito e em repouso</li>
                  <li>Autenticação segura com hash de senhas</li>
                  <li>Acesso restrito a dados pessoais por nossa equipe</li>
                  <li>Monitoramento regular de segurança</li>
                  <li>Backup seguro de dados</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                6. Seus Direitos
              </h2>
              <div className="text-gray-700 dark:text-gray-300 space-y-4">
                <p>Você tem os seguintes direitos em relação às suas informações pessoais:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Acesso:</strong> Solicitar uma cópia dos dados que temos sobre você</li>
                  <li><strong>Correção:</strong> Solicitar correção de informações imprecisas</li>
                  <li><strong>Exclusão:</strong> Solicitar a exclusão de suas informações pessoais</li>
                  <li><strong>Portabilidade:</strong> Solicitar transferência de seus dados</li>
                  <li><strong>Objeção:</strong> Opor-se ao processamento de seus dados</li>
                  <li><strong>Limitação:</strong> Solicitar limitação do processamento</li>
                </ul>
                <p>
                  Para exercer esses direitos, entre em contato conosco através dos canais disponibilizados 
                  na plataforma.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                7. Cookies e Tecnologias Similares
              </h2>
              <div className="text-gray-700 dark:text-gray-300 space-y-4">
                <p>
                  Utilizamos cookies e tecnologias similares para melhorar sua experiência na plataforma:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Cookies Essenciais:</strong> Necessários para o funcionamento da plataforma</li>
                  <li><strong>Cookies de Preferência:</strong> Lembram suas configurações e preferências</li>
                  <li><strong>Cookies Analíticos:</strong> Nos ajudam a entender como você usa a plataforma</li>
                </ul>
                <p>
                  Você pode gerenciar suas preferências de cookies através das configurações do seu navegador.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                8. Retenção de Dados
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Mantemos suas informações pessoais pelo tempo necessário para fornecer nossos serviços 
                e cumprir obrigações legais. Quando você excluir sua conta, seus dados pessoais serão 
                removidos de nossos sistemas, exceto quando exigido por lei.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                9. Transferências Internacionais
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Seus dados podem ser transferidos e processados em países diferentes do seu país de residência. 
                Quando isso ocorrer, implementamos salvaguardas adequadas para proteger suas informações 
                de acordo com esta política de privacidade.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                10. Menores de Idade
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Nossa plataforma não é direcionada a menores de 13 anos. Não coletamos conscientemente 
                informações pessoais de crianças menores de 13 anos. Se tomarmos conhecimento de que 
                coletamos dados de uma criança menor de 13 anos, tomaremos medidas para excluir essas informações.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                11. Alterações nesta Política
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Podemos atualizar esta Política de Privacidade periodicamente. Quando fizermos alterações 
                significativas, notificaremos você através da plataforma ou por e-mail. A data da última 
                atualização será sempre indicada no topo desta política.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                12. Contato
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Se você tiver dúvidas sobre esta Política de Privacidade ou quiser exercer seus direitos, 
                entre em contato conosco através da plataforma ou pelos meios de contato disponibilizados. 
                Responderemos às suas solicitações dentro de um prazo razoável.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                13. Conformidade Legal
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Esta Política de Privacidade está em conformidade com a Lei Geral de Proteção de Dados (LGPD) 
                do Brasil e outras leis de privacidade aplicáveis. Estamos comprometidos em manter a 
                conformidade com todas as regulamentações relevantes de proteção de dados.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
